import { createApi } from '@reduxjs/toolkit/query/react';
import { BASE_URL, API_VERSION, authHeaders, fetchWithAuth, mapUserProjectRow } from '../api';

// Use this instead of a `data: x = []` destructuring default wherever a
// query's `data` feeds a useMemo/useEffect chain. A destructuring default
// creates a brand-new [] on every render for as long as `data` is undefined
// (before the first fetch resolves) — an unstable reference that cascades
// through useMemo → useEffect → setState into an infinite render loop
// ("Maximum update depth exceeded"). This constant is referentially stable,
// so `data ?? EMPTY_ARRAY` isn't.
export const EMPTY_ARRAY = [];

// RTK Query cache for read endpoints that should behave "cache-first, then
// background-refresh": a component's hook returns whatever's already in the
// Redux cache immediately (no spinner, no waiting on the network) and RTK
// Query kicks off a real fetch behind it, updating the cache — and every
// subscribed component — the moment fresh data arrives.
//
// This app's auth (bearer token + silent refresh-on-401) doesn't fit RTK
// Query's built-in fetchBaseQuery, so this adapts the existing fetchWithAuth/
// authHeaders helpers from api.js into the shape createApi expects instead of
// duplicating that logic here.
const rawBaseQuery = () => async ({ url, method = 'GET', body }) => {
  try {
    const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}${url}`, {
      method,
      headers: authHeaders(),
      body: body != null ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const data = await res.text().catch(() => null);
      return { error: { status: res.status, data } };
    }
    const data = res.status === 204 ? null : await res.json();
    return { data };
  } catch (err) {
    return { error: { status: 'FETCH_ERROR', data: err.message } };
  }
};

export const futurizerApi = createApi({
  reducerPath: 'futurizerApi',
  baseQuery: rawBaseQuery(),
  tagTypes: ['UserProjects', 'Customers', 'CodeList', 'ProjectCommands', 'CommandQueue'],
  endpoints: builder => ({
    // GET /User/userproject/{userId} — the current user's assigned projects.
    // refetchOnMountOrArgChange: every time a component mounts this hook, RTK
    // Query still returns the cached value first (if any) but always fires a
    // background refetch too, rather than trusting a possibly-stale cache
    // indefinitely. This list changes often enough (new tasks processed, new
    // projects assigned) that it's worth the extra request on every mount.
    getUserProjects: builder.query({
      query: userId => ({ url: `/User/userproject/${userId}` }),
      transformResponse: rows => (rows ?? []).map(mapUserProjectRow),
      providesTags: ['UserProjects'],
      refetchOnMountOrArgChange: true,
    }),

    // GET /Customer and /CodeList back the create/edit-project modal's
    // dropdowns. These change rarely, so — unlike getUserProjects — they use
    // RTK Query's normal default: fetch once, then serve every later mount
    // from cache for the rest of the session (no forced refetch each time the
    // modal opens). That also avoids re-running the modal's one-time default-
    // selection logic mid-edit, which a forced background refetch would risk.
    getCustomers: builder.query({
      query: () => ({ url: '/Customer' }),
      transformResponse: rows => rows ?? [],
      providesTags: ['Customers'],
    }),
    getCodeList: builder.query({
      query: () => ({ url: '/CodeList' }),
      transformResponse: rows => rows ?? [],
      providesTags: ['CodeList'],
    }),

    // GET /Project/{projectId}/commands and GET /Command/queue/project/{projectId}
    // back AIProcessPage's Commands/Tasks views. The queue changes whenever a
    // background task moves through QUEUED → PROCESSING → PROCESSED/FAILED, so
    // callers pass `pollingInterval` (see AIProcessPage) to keep it live —
    // RTK Query's built-in polling, replacing what used to be a hand-rolled
    // setInterval.
    getProjectCommands: builder.query({
      query: projectId => ({ url: `/Project/${projectId}/commands` }),
      transformResponse: rows => rows ?? [],
      providesTags: ['ProjectCommands'],
    }),
    getCommandQueue: builder.query({
      query: projectId => ({ url: `/Command/queue/project/${projectId}` }),
      transformResponse: rows => rows ?? [],
      providesTags: ['CommandQueue'],
    }),
  }),
});

export const {
  useGetUserProjectsQuery,
  useGetCustomersQuery,
  useGetCodeListQuery,
  useGetProjectCommandsQuery,
  useGetCommandQueueQuery,
} = futurizerApi;
